import { useEffect, useState } from 'react'
import { Award } from 'lucide-react'
import { FormDrawer } from '../../components/Drawer'
import FileField from '../../components/FileField'
import { FormBody, FormCols, FormSection } from '../../components/Form'
import FormRow from '../../components/FormRow'
import TextField from '../../components/TextField'
import { certificatesStrings as S } from './strings'

const EMPTY = { name: '', nameEn: '', prefix: '', fileAr: null, fileEn: null }

/**
 * نموذج جديد / تعديل — the certificate template form.
 *
 * The legacy posts this through a hidden iframe because the two artwork files
 * ride along with the text fields (`enctype="multipart/form-data"`,
 * `target="upload_target"`), and answers with a page that calls
 * `window.parent.finishUpload(...)`. None of that shape survives the port: the
 * fields are the same, the transport is the service's problem.
 *
 * Its two columns are kept — what the template is CALLED on the left, what it
 * is MADE OF on the right — because the artwork is the part an admin replaces
 * most often and it deserves its own half rather than a third upload row.
 */
export default function CertificateFormDrawer({ open, certificate, onClose, onSave }) {
  const [values, setValues] = useState(EMPTY)
  const [errors, setErrors] = useState({})

  useEffect(() => {
    if (!open) return
    setValues(certificate ? { ...EMPTY, ...certificate } : EMPTY)
    setErrors({})
  }, [open, certificate])

  const set = (key) => (value) => {
    setValues((previous) => ({ ...previous, [key]: value }))
    setErrors((previous) => ({ ...previous, [key]: undefined }))
  }

  const submit = (event) => {
    event.preventDefault()
    const next = {}
    /* The legacy's own rules: both names 2–300 characters, the prefix optional
       but 1–100 when given, and the artwork required only when creating. */
    const len = (value) => value.trim().length
    if (!len(values.name)) next.name = S.form.required
    else if (len(values.name) < 2 || len(values.name) > 300) next.name = S.form.lengthError
    if (!len(values.nameEn)) next.nameEn = S.form.required
    else if (len(values.nameEn) < 2 || len(values.nameEn) > 300) next.nameEn = S.form.lengthError
    if (values.prefix && len(values.prefix) > 100) next.prefix = S.form.prefixError
    if (!certificate && !values.fileAr) next.fileAr = S.form.required
    if (!certificate && !values.fileEn) next.fileEn = S.form.required

    setErrors(next)
    if (Object.keys(next).length > 0) return
    onSave?.(values)
  }

  return (
    <FormDrawer
      open={open}
      size="lg"
      formId="certificate-form"
      icon={Award}
      title={certificate ? S.row.edit : S.actions.create}
      subtitle={S.title}
      submitLabel={S.form.save}
      cancelLabel={S.close}
      onClose={onClose}
      onSubmit={submit}
    >
      {/* What the template is CALLED, then what it is MADE OF. The two used to
          be columns, which left the Arabic name lining up against a file picker
          and the whole right half empty below it. As stacked sections each pair
          sits on its own baseline. */}
      <FormBody>
        <FormSection>
          <FormCols>
            <FormRow label={S.form.nameAr} error={errors.name}>
              {(id) => (
                <TextField
                  id={id}
                  value={values.name}
                  onChange={set('name')}
                  invalid={!!errors.name}
                  autoComplete="off"
                />
              )}
            </FormRow>

            <FormRow label={S.form.nameEn} error={errors.nameEn}>
              {(id) => (
                <TextField
                  id={id}
                  dir="ltr"
                  value={values.nameEn}
                  onChange={set('nameEn')}
                  invalid={!!errors.nameEn}
                  autoComplete="off"
                />
              )}
            </FormRow>

            {/* The legacy's placeholder differs from its label on purpose — the
                label names the field, the placeholder shows what goes in it. */}
            <FormRow label={S.form.prefix} error={errors.prefix} hint={S.form.prefixHint}>
              {(id) => (
                <TextField
                  id={id}
                  dir="ltr"
                  value={values.prefix}
                  onChange={set('prefix')}
                  placeholder={S.form.prefixPlaceholder}
                  invalid={!!errors.prefix}
                  autoComplete="off"
                />
              )}
            </FormRow>
          </FormCols>
        </FormSection>

        <FormSection>
          <FormCols>
            <FormRow label={S.form.fileAr} error={errors.fileAr} hint={S.form.fileSpec}>
              {(id) => <FileField id={id} accept="image/png" onChange={set('fileAr')} />}
            </FormRow>

            <FormRow label={S.form.fileEn} error={errors.fileEn} hint={S.form.fileSpec}>
              {(id) => <FileField id={id} accept="image/png" onChange={set('fileEn')} />}
            </FormRow>
          </FormCols>
        </FormSection>
      </FormBody>
    </FormDrawer>
  )
}
